import { createContext, useContext, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { MeResponse } from '../../shared/api-types.js';
import { api } from '../api.js';
import { FailureView, StateView } from '../ui/StateView.js';
import { useRequest } from '../ui/use-request.js';
import type { SectionStripItem } from '../ui/SectionStrip.js';
import { SignInPage } from './pages.js';
import { PublicShell } from './PublicShell.js';

export interface MeContextValue {
  me: MeResponse;
  /** Re-fetches /api/me, e.g. after a change that could alter capabilities. */
  reload: () => void;
}

const MeContext = createContext<MeContextValue | null>(null);

/**
 * Loads GET /api/me once and provides it to the authenticated app. A 401 — no session — renders
 * the sign-in page in place, inside the public shell, at whatever URL was visited: no redirect, so
 * the address bar and the button's returnTo are the page the person wanted. Other failures render
 * a recoverable state instead of an empty shell. Children render only once `me` is known.
 */
export function MeProvider({ children, loading }: { children: ReactNode; loading?: ReactNode }) {
  // Why noRedirect: the wrapper answers a 401 with a reload, which here would loop; this is the
  // one request whose 401 is decided by rendering rather than by reloading.
  const request = useRequest<MeResponse>((signal) => api.get<MeResponse>('/api/me', { signal, noRedirect: true }), []);
  // Why the router's location and not window's: the sign-in page stays mounted across a client-side
  // navigation (the top bar's mark is a router link to /), and only a subscribed location keeps
  // both sign-in controls' returnTo on the URL the address bar shows.
  const location = useLocation();
  if (request.status === 'loading') {
    return <>{loading ?? <StateView kind="loading" label="Loading Calis" minHeight="50dvh" />}</>;
  }
  if (request.status === 'error' && request.error?.status === 401) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return (
      <PublicShell signInReturnTo={returnTo}>
        <SignInPage returnTo={returnTo} />
      </PublicShell>
    );
  }
  if (request.status === 'error' || !request.data) {
    return (
      <div className="page page--reading">
        <FailureView failure={request.error ?? { ok: false, status: 0, code: 'unexpected_response', message: 'No response.', correlationId: null }} what="your account" onRetry={request.reload} minHeight="50dvh" />
      </div>
    );
  }
  return <MeContext.Provider value={{ me: request.data, reload: request.reload }}>{children}</MeContext.Provider>;
}

/** The signed-in user and what the client may offer them. Selection only — never enforcement. */
export function useMe(): MeResponse {
  const context = useContext(MeContext);
  if (!context) throw new Error('useMe must be used inside MeProvider (an authenticated route)');
  return context.me;
}

export function useMeContext(): MeContextValue {
  const context = useContext(MeContext);
  if (!context) throw new Error('useMeContext must be used inside MeProvider (an authenticated route)');
  return context;
}

/**
 * `costs` is the AI call ledger's page, and `queue` the worker's; both are read under the audit section
 * (owner rulings of 2026-09-27 and, for the queue, ruling 7 of 2026-09-28).
 */
export type AdminSectionId = 'users' | 'groups' | 'audit' | 'costs' | 'queue';

/** The administration sections this user holds, in module order. Never lists what they cannot reach. */
export function adminSectionsFor(me: MeResponse): SectionStripItem[] {
  const items: SectionStripItem[] = [];
  if (me.admin.users.view) items.push({ label: 'Users', to: '/admin/users' });
  if (me.admin.groups) items.push({ label: 'Groups', to: '/admin/groups' });
  if (me.admin.audit.view) items.push({ label: 'Audit log', to: '/admin/audit' });
  if (me.admin.audit.view) items.push({ label: 'Costs', to: '/admin/costs' });
  if (me.admin.audit.view) items.push({ label: 'Queue', to: '/admin/queue' });
  return items;
}

export function holdsAdminSection(me: MeResponse, section: AdminSectionId): boolean {
  switch (section) {
    case 'users':
      return me.admin.users.view;
    case 'groups':
      return me.admin.groups;
    case 'audit':
    case 'costs':
    case 'queue':
      return me.admin.audit.view;
    default:
      return false;
  }
}

/** The first administration section the user holds, or null when administration is not theirs. */
export function adminHome(me: MeResponse): string | null {
  return adminSectionsFor(me)[0]?.to ?? null;
}
